import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { KeyRound, MailCheck } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Logo } from "@/components/site/Header";
import { useSeo } from "@/hooks/useSeo";

const input = "w-full h-12 rounded-lg border border-slate-200 px-4 focus:border-emerald-500 outline-none";
const button = "w-full h-12 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white font-bold rounded-lg transition-colors";

const Shell = ({ title, sub, children }) => (
  <div className="min-h-screen flex items-center justify-center p-6 bg-white" data-testid="admin-password-page">
    <div className="w-full max-w-sm space-y-5">
      <Logo />
      <div>
        <h1 className="text-3xl font-extrabold tracking-tight flex items-center gap-2"><KeyRound size={22} /> {title}</h1>
        <p className="text-sm text-slate-500 mt-1">{sub}</p>
      </div>
      {children}
      <p className="text-center text-sm"><Link to="/admin/login" className="text-emerald-700 font-semibold hover:underline" data-testid="back-to-login-link">Back to sign in</Link></p>
    </div>
  </div>
);

export const ForgotPassword = () => {
  useSeo({ title: "Forgot password", noindex: true });
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { setSent((await api.post("/auth/forgot-password", { email })).data.message); } catch (er) { toast.error(errMsg(er)); } finally { setBusy(false); }
  };
  return (
    <Shell title="Forgot password" sub="Enter your team email. We'll email a one-time link to choose a new password.">
      {sent ? (
        <p className="text-sm bg-emerald-50 border border-emerald-200 rounded-lg p-4 flex gap-2" data-testid="forgot-password-sent"><MailCheck size={18} className="shrink-0 text-emerald-700" /> {sent} Check your spam folder too.</p>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <input required type="email" placeholder="Email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} className={input} data-testid="forgot-email-input" />
          <button disabled={busy} className={button} data-testid="forgot-submit-button">{busy ? "Sending…" : "Email me a reset link"}</button>
        </form>
      )}
    </Shell>
  );
};

export const ResetPassword = () => {
  useSeo({ title: "Choose a new password", noindex: true });
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [f, setF] = useState({ password: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const token = params.get("token") || "";
  const submit = async (e) => {
    e.preventDefault();
    if (f.password.length < 10) return toast.error("Use at least 10 characters");
    if (f.password !== f.confirm) return toast.error("The two passwords don't match");
    setBusy(true);
    try {
      toast.success((await api.post("/auth/reset-password", { token, password: f.password })).data.message);
      nav("/admin/login");
    } catch (er) { toast.error(errMsg(er), { duration: 8000 }); } finally { setBusy(false); }
  };
  if (!token) return <Shell title="Reset link missing" sub="Open the link from your email, or ask for a new one."><Link to="/admin/forgot-password" className={`${button} grid place-items-center`}>Ask for a new link</Link></Shell>;
  return (
    <Shell title="Choose a new password" sub="At least 10 characters. Signing in elsewhere will stop working once you change it.">
      <form onSubmit={submit} className="space-y-4">
        <input required type="password" placeholder="New password" autoComplete="new-password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className={input} data-testid="reset-password-input" />
        <input required type="password" placeholder="Type it again" autoComplete="new-password" value={f.confirm} onChange={(e) => setF({ ...f, confirm: e.target.value })} className={input} data-testid="reset-confirm-input" />
        <button disabled={busy} className={button} data-testid="reset-submit-button">{busy ? "Saving…" : "Save new password"}</button>
      </form>
    </Shell>
  );
};
